import { FC, useEffect, useState, useRef } from 'react';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { Post } from '@libs/models';
import { useLazySearchJobseekerPostsQuery, useGetJobseekerPostQuery } from '@carcareers/store';
import { JobseekerPostView, JobseekerPostInfiniteScroll } from '@carcareers/components';
import { subscribeToMobileStateChanges, getFilterParams, useIsMobile, JobPosition } from '@carcareers/utils';

export interface JobSearchPost extends Post {
  applied?: boolean;
  saved?: boolean;
}

export const JobSearch: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: urlParamId } = useParams();
  const location = useLocation();
  const postID = urlParamId || searchParams.get('postID');

  const [posts, setPosts] = useState<Record<string, JobSearchPost>>({});
  const [numPosts, setNumPosts] = useState(0);
  const [selectedPost, setSelectedPost] = useState<JobSearchPost | null>(null);
  const prevSelectedPostRef = useRef<JobSearchPost | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [prevSearchParams, setPrevSearchParams] = useState<URLSearchParams | null>(null);
  const mobile = useIsMobile();

  const [getJobseekerPosts] = useLazySearchJobseekerPostsQuery();
  const { data: specificPost, isSuccess: isSpecificPostSuccess } = useGetJobseekerPostQuery(
    { id: Number(postID) },
    { skip: !postID },
  );

  const getPosts = async (useQueryParams = false, resetQueryParams = false) => {
    const filters = resetQueryParams ? {} : getFilterParams(searchParams);

    if ((loading || !hasMore) && !useQueryParams) return;
    setLoading(true);

    try {
      const newPage = useQueryParams ? 1 : page + 1;
      const { data, isSuccess } = await getJobseekerPosts({ page: newPage, limit: 10, filters });

      if (isSuccess && data) {
        const newPosts: Record<string, JobSearchPost> = {};

        data.items.forEach((item) => {
          newPosts[`${item.id} ${item.job?.title}`] = item;
        });

        if (!selectedPost && !prevSelectedPostRef.current && !postID && !mobile) {
          setSelectedPost(newPosts[`${data.items[0].id} ${data.items[0].job?.title}`]);
        }

        if (useQueryParams) {
          setPosts(newPosts);
          setNumPosts(data.items.length);
        } else {
          setPosts((prevPosts) => ({ ...prevPosts, ...newPosts }));
          setNumPosts((prevNumPosts) => prevNumPosts + data.items.length);
        }

        setHasMore(newPage < (data.meta.totalPages || 0));
        setPage(newPage);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (numPosts === 0) {
      getPosts(false, true);
    }
  }, [numPosts]);

  useEffect(() => {
    if (prevSearchParams !== searchParams) {
      getPosts(true);
      setPrevSearchParams(searchParams);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedPost) {
      prevSelectedPostRef.current = selectedPost;
    }
  }, [selectedPost]);

  useEffect(() => {
    if (isSpecificPostSuccess && specificPost) {
      setSelectedPost(specificPost);
    }
  }, [isSpecificPostSuccess, specificPost]);

  useEffect(() => {
    const unsubscribe = subscribeToMobileStateChanges((mobile: boolean) => {
      if (!mobile && !selectedPost && prevSelectedPostRef.current) {
        setSelectedPost(prevSelectedPostRef.current);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [selectedPost]);

  useEffect(() => {
    if (!mobile && !selectedPost && Object.keys(posts).length > 0) {
      const firstPostKey = Object.keys(posts)[0];
      setSelectedPost(posts[firstPostKey]);
    }
  }, [mobile, selectedPost, posts]);

  useEffect(() => {
    if (location.state?.searchPositions && location.state.searchPositions.length > 0) {
      const positions = location.state.searchPositions as (JobPosition | string)[];
      const keywords = positions.map(position => {
        if (typeof position === 'string') {
          return position;
        }
        return position.position;
      }).join(' ');
      
      if (keywords && !searchParams.get('keyword')) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('keyword', keywords);
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [location.state, searchParams, setSearchParams]);

  return (
    <Box sx={{ maxHeight: 'calc(100vh - 136px)' }}>
      <Grid container>
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: selectedPost ? 'none' : 'block', md: 'block' } }}>
          <JobseekerPostInfiniteScroll
            posts={posts}
            numPosts={numPosts}
            hasMore={hasMore}
            selectedPost={selectedPost}
            getPosts={getPosts}
            setSelectedPost={setSelectedPost}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: { xs: selectedPost ? 'block' : 'none', md: 'block' } }}>
          <JobseekerPostView
            post={selectedPost}
            prevPost={prevSelectedPostRef.current}
            setPosts={setPosts}
            setSelectedPost={setSelectedPost}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default JobSearch;
